import React from 'react';
import type { ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { EmailRule } from './useData';
import { formatActionLabel, formatConditionLabel, formatMatchLabel } from './emailRuleLabels';

export function getEmailRuleStatusBadges(rule: EmailRule, _colors?: ThemeColors) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
        rule.enabled
          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          : 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300',
      )}
    >
      {rule.enabled ? 'Enabled' : 'Disabled'}
    </span>
  );
}

export function getEmailRuleDetailSections(
  rule: EmailRule,
  formatDate: (dateString?: string) => string,
  context: {
    accountById: Map<number, string>;
    personalFolderNames: Map<number, string>;
    userNames: Map<number, string>;
  },
) {
  const conditions = rule.conditions?.rules ?? [];
  const actions = rule.actions?.actions ?? [];
  const accountLabel =
    rule.emailAccountId != null
      ? context.accountById.get(rule.emailAccountId) ?? `Account #${rule.emailAccountId}`
      : 'All accounts';

  const conditionsValue =
    conditions.length > 0 ? (
      <ul className="space-y-1 text-sm">
        {conditions.map((condition, index) => (
          <li key={index} className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-indigo-500">•</span>
            <span>{formatConditionLabel(condition)}</span>
          </li>
        ))}
      </ul>
    ) : (
      <span className="text-sm text-gray-500">No conditions</span>
    );

  const actionsValue =
    actions.length > 0 ? (
      <ul className="space-y-1 text-sm">
        {actions.map((action, index) => (
          <li key={index} className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-emerald-600">→</span>
            <span>{formatActionLabel(action, context.personalFolderNames, context.userNames)}</span>
          </li>
        ))}
      </ul>
    ) : (
      <span className="text-sm text-gray-500">No actions</span>
    );

  return [
    {
      title: 'Overview',
      fields: [
        { label: 'Name', value: rule.name?.trim() || `Rule #${rule.id}` },
        { label: 'Status', value: rule.enabled ? 'Enabled' : 'Disabled' },
        { label: 'Priority', value: String(rule.priority) },
        { label: 'Account', value: accountLabel },
        { label: 'Match mode', value: formatMatchLabel(rule.conditions.match) },
        {
          label: 'Note',
          value: 'Not applied during email sync',
          condition: !rule.enabled,
        },
      ],
    },
    {
      title: 'When',
      fields: [{ label: 'Conditions', value: conditionsValue }],
    },
    {
      title: 'Then',
      fields: [{ label: 'Actions', value: actionsValue }],
    },
    {
      title: 'Metadata',
      fields: [
        { label: 'Created', value: formatDate(rule.createdAt) },
        { label: 'Updated', value: formatDate(rule.updatedAt) },
      ],
    },
  ];
}
